/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useRipeningRoomRun } from "../../../../hooks/useRipeningPrograms";
import { getRoomAvailability, RoomAvailabilityColor } from "./roomAvailability";

// * Bootstrap has no purple theme colour.
const purpleCss = css`color: color-mix(in srgb, var(--bs-purple, #6f42c1) 80%, var(--bs-body-color, #000));`;

const textClass = (color: RoomAvailabilityColor) => (color === "purple" ? "fw-semibold" : `fw-semibold text-${color}-emphasis`);

/** A ripening room in the room list, its meta flattened onto it (formatMeta2Frontend). */
export interface RoomListLocation {
    location_id: string;
    tenant_id?: string | null;
    gateway?: string | null;
    controller_room?: string | null;
    pallets?: number | null;
    // * Set while a ripening cycle is open in the room.
    active_cycle_id?: string | null;
}

/** The room's status in words, for the room overview: the same wording as the room's status card. */
export default function RipeningRoomStatusText({ location, clientHasLinkedRooms }: { location: RoomListLocation; clientHasLinkedRooms: boolean }) {
    const gatewayId = location.gateway || null;
    const roomId = location.controller_room || null;
    const { data, isError } = useRipeningRoomRun(gatewayId, roomId, { tenantId: location.tenant_id });
    const availability = getRoomAvailability({
        linked: !!gatewayId && !!roomId,
        clientHasLinkedRooms,
        status: data,
        run: isError ? null : data?.run,
        statusError: isError,
        palletCount: typeof location.pallets === "number" ? location.pallets : 0,
        hasActiveCycle: !!location.active_cycle_id,
    });
    if (!availability) return null;
    return (
        <span
            className={textClass(availability.color)}
            css={availability.color === "purple" ? purpleCss : undefined}
            title={availability.detail ?? undefined}
            data-testid={`room-status-${location.location_id}`}
            data-kind={availability.kind}
        >
            {availability.label}
        </span>
    );
}
